import { Field, useFormikContext } from 'formik';
import React, { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { DEF_PG_SIZE_DROP_DOWN_MIN, DEF_PG_SIZE_SUB_TABS } from '../../constants/maximum-page-size';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import { ListQuery } from '../../models/ListQuery';
import { getDeviceActionNamesForDoorWithPagination } from '../../services/deviceActionService';
import { getDeviceImproxNames } from '../../services/deviceImproxService';
import { getDeviceTimePatternNamesWithPagination } from '../../services/deviceTimePatternService';
import { IAppState } from '../../store/types';
import { appendFilter } from '../../utilities/filters';
import { requiredValidation } from '../../utilities/Validation';
import Label from '../label/label';
import PaginatedSearchableSelectField from '../selection-field/paginated-searchable-select-field';

export const TimeTriggeredActionCard: FC = () => {
    const { translate } = useLocale();
    const { values, errors, setFieldValue } = useFormikContext<any>();
    const siteName = useSelector((state: IAppState) => state.site.site.label);
    const siteId = useSelector((state: IAppState) => state.site.site.value);

    return <>
        <div className="flex flex-row mb-2 mr-6">
            <div className="flex flex-col flex-1 pr-3">
                <div className="flex flex-row justify-between" id='nameLabelId'>
                    <Label
                        htmlFor="name"
                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.name)}:</Label>
                    {errors.name ? <span className='text-red-600 text-xs'>{errors.name}</span> : null}
                </div>
                <Field id='nameInputFieldId'
                    type="text"
                    name="name"
                    autoFocus
                    className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                    validate={(value) => requiredValidation(value, translate)} />
            </div>
        </div>
        <div className="flex flex-row mb-2 mr-6">
            <div className="flex flex-col flex-1 pr-3">
                <div className="flex flex-row justify-between">
                    <Label
                        htmlFor="site"
                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.site)}:</Label>
                </div>
                {values.id
                    ? <Field
                        value={values.site}
                        disabled={true}
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
                    : <Field
                        value={siteName}
                        disabled={true}
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />}
            </div>
        </div>
        <span className="mb-2 text-sm"><b>{translate.byKey(TranslationKeys.activePeriod)}</b></span>
        <div className="flex flex-row mb-2 mr-6">
            <div className="flex flex-col flex-1 pr-3">
                <div className="flex flex-row justify-between" id='timePatternLabelId'>
                    <Label htmlFor='timePatternId'
                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.deviceTimePattern)}:</Label>
                    {errors.timePatternId ? <span className='text-red-600 text-xs'>{errors.timePatternId}</span> : null}
                </div>
                <span id='timePatternDropDownId'>
                    <PaginatedSearchableSelectField
                        name={translate.byKey(TranslationKeys.deviceTimePattern)}
                        key="timePatternId"
                        accessor='timePatternId'
                        pageSize={DEF_PG_SIZE_DROP_DOWN_MIN}
                        dataMethod={(listParameters?: ListQuery) => getDeviceTimePatternNamesWithPagination({ ...listParameters, filters: appendFilter(values.siteId ?? siteId, 'Site id', 'site.id', listParameters?.filters ?? []) })}
                        searchAccessor={'tpName'}
                        required={true}
                        onSelect={(event, item) => {
                            setFieldValue('timePattern', item?.label)
                            setFieldValue('timePatternId', item?.value)
                        }}
                        selectedItemLabel={values.timePattern} />
                </span>
            </div>
        </div>
        <span className="mb-2 mt-1 text-sm"><b>{translate.byKey(TranslationKeys.actionToExecute)}</b></span>
        <div className="flex flex-row mb-2 mr-6">
            <div className="flex flex-col flex-1 pr-3">
                <div className="flex flex-row justify-between" id='deviceId'>
                    <Label htmlFor='deviceId'
                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.device)}:</Label>
                    {errors.deviceId ? <span className='text-red-600 text-xs'>{errors.deviceId}</span> : null}
                </div>
                <span id='deviceDropDownId'>
                     <PaginatedSearchableSelectField
                         name={translate.byKey(TranslationKeys.device)}
                         key="deviceId"
                         accessor='deviceId'
                         dataMethod={(listParameters?: ListQuery) => getDeviceImproxNames({ ...listParameters, filters: appendFilter(values.siteId ?? siteId, 'Site id', 'site.id', listParameters?.filters ?? []) })}
                         searchAccessor={'devName'}
                         required={true}
                         onSelect={(event, item) => {
                             setFieldValue('device', item?.label);
                             setFieldValue('deviceActionId', '');
                             setFieldValue('deviceId', item?.value);
                         }}
                         selectedItemLabel={values.device}
                         pageSize={DEF_PG_SIZE_DROP_DOWN_MIN} />
                </span>
            </div>
        </div>
        <div className="flex flex-row mb-2 mr-6">
            <div className="flex flex-col flex-1 pr-3">
                <div className="flex flex-row justify-between" id='deviceActionIdLabelId'>
                    <Label htmlFor='deviceActionId'
                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.deviceAction)}:</Label>
                    {errors.deviceActionId ? <span className='text-red-600 text-xs'>{errors.deviceActionId}</span> : null}
                </div>
                <span id='deviceActionDropDownId'>
                    <PaginatedSearchableSelectField
                        name={translate.byKey(TranslationKeys.deviceAction)}
                        key="deviceActionId"
                        accessor='deviceActionId'
                        dataMethod={getDeviceActionNamesForDoorWithPagination}
                        searchAccessor={'setting.trnName'}
                        onSelect={(event, item) => {
                            setFieldValue('deviceAction', item?.label);
                            setFieldValue('deviceActionId', item?.value);
                        }}
                        disabled={!(values.deviceId)}
                        required={true}
                        dependent={{ accessor: 'deviceId', name: 'deviceId', key: 'deviceImprox.id', value: values.deviceId, childAcceccor: 'deviceActionId' }}
                        pageSize={DEF_PG_SIZE_DROP_DOWN_MIN-1}
                        selectedItemLabel={values.deviceAction} />
                </span>
            </div>
        </div>
    </>
};
